/*
 * Copyright (c) 2026 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.visflux.chord

import org.molap.network.NetworkListener
import org.molap.network.WeightedNetwork

/** The network a chord diagram shows: its nodes and their links, with the weights of the links. */
interface ChordNetwork<Node, Link> {
    val nodes: Iterable<Node>

    /** Whether the arcs of the nodes are made of their outgoing links only. */
    val isDirected: Boolean

    fun getFrom(link: Link): Node
    fun getTo(link: Link): Node

    /** The link in the other direction, which may not exist. */
    fun getReverse(link: Link): Link

    /** The weight of a link, null for a link that does not exist. */
    fun getWeight(link: Link): Any?

    fun getEdgesFrom(node: Node): Iterable<Link>

    /** The links leaving a node, and those entering it unless the reverse link exists. */
    fun getEdges(node: Node): Iterable<Link>

    fun addNetworkListener(listener: NetworkListener)
    fun removeNetworkListener(listener: NetworkListener)
}

/** A weighted network of MOLAP as the network of a chord diagram. */
class WeightedChordNetwork<Node, Link>(private val network: WeightedNetwork<Node, Link, Any>) : ChordNetwork<Node, Link> {
    override val nodes: Iterable<Node>
        get() = network.nodes

    override val isDirected: Boolean
        get() = network.getDirectedSelection().selected == true

    override fun getFrom(link: Link): Node = network.getFrom(link)

    override fun getTo(link: Link): Node = network.getTo(link)

    override fun getReverse(link: Link): Link = network.getReverse(link)

    override fun getWeight(link: Link): Any? = network.getWeight(link)

    override fun getEdgesFrom(node: Node): Iterable<Link> = network.getDirectedLinks(node)

    override fun getEdges(node: Node): Iterable<Link> = network.getLinks(node)

    override fun addNetworkListener(listener: NetworkListener) = network.addNetworkListener(listener)

    override fun removeNetworkListener(listener: NetworkListener) = network.removeNetworkListener(listener)
}
